import { formatPesoAmount } from '../utils/money';
import usePrefilledVarietyFormats from '../hooks/usePrefilledVarietyFormats';
import { buildFormatPayload } from './productCreation/productCreationDrafts';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { addVarietyToProduct, uploadFile } from '../api';
import { normalizeVarietyGroups } from '../utils/varietyGroups';

const NEW_GROUP_ID = 'new';

export const formatMoney = (value) => `$${formatPesoAmount(Number(value || 0))}`;

export const formatPercent = (value) => {
    const number = Number(value);
    if (!Number.isFinite(number)) return null;
    const rounded = Math.round(number * 10) / 10;
    return `${Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1)}%`;
};

export const buildStockPriceRulePayload = (rule) => {
    if (!rule || rule.mode === 'none') return null;
    const value = String(rule.value ?? '').trim();
    if (value === '') return null;
    return { mode: rule.mode, value: Number(value), basis: rule.basis === 'kg' ? 'kg' : 'unit' };
};

const NewVarietyModal = ({
    isOpen,
    onClose,
    product,
    branchId: branchIdProp,
    initialGroupId,
    onVarietyCreated,
    canUpdateProduct = true,
}) => {
    const routeBranchId = useParams().branchId;
    const branchId = branchIdProp ?? routeBranchId ?? localStorage.getItem('selectedBranchId');
    const [varietyName, setVarietyName] = useState('');
    const [description, setDescription] = useState('');
    const [inventoryBaseUnit, setInventoryBaseUnit] = useState('kg');
    const [file, setFile] = useState(null);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [groupId, setGroupId] = useState('');
    const [newGroupName, setNewGroupName] = useState('');
    const openedProductId = useRef(null);
    const groups = useMemo(() => normalizeVarietyGroups(product?.VarietyGroups), [product?.VarietyGroups]);
    const [drafts, setDrafts] = useState([{ _draftKey: 'new-variety', formats: [] }]);
    const defaults = usePrefilledVarietyFormats({ branchId, categoryId: product?.categoryId || product?.Category?.id, varieties: drafts, setVarieties: setDrafts, enabled: isOpen });

    useEffect(() => {
        if (!isOpen) {
            openedProductId.current = null;
            return;
        }
        if (!product || openedProductId.current === product.id) return;
        openedProductId.current = product.id;
        setVarietyName('');
        setDrafts([{ _draftKey: 'new-variety', formats: [] }]);
        setDescription('');
        setInventoryBaseUnit(product?.Category?.isResaleOnly ? 'unidad' : 'kg');
        setFile(null);
        setError('');
        setGroupId(groups.some((group) => String(group.id) === String(initialGroupId)) ? String(initialGroupId) : '');
        setNewGroupName('');
    }, [isOpen, product, groups, initialGroupId]);

    if (!isOpen || !product) return null;

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (saving || !canUpdateProduct) return;
        const name = varietyName.trim();
        if (!name) {
            setError('El nombre de la variedad es obligatorio.');
            return;
        }
        if (defaults.loading || defaults.error) return;
        const groupName = newGroupName.trim().replace(/\s+/g, ' ');
        if (groupId === NEW_GROUP_ID && !groupName) {
            setError('El nombre del grupo es obligatorio.');
            return;
        }
        if (groupId === NEW_GROUP_ID && groups.some((group) => group.name.toLocaleLowerCase('es') === groupName.toLocaleLowerCase('es'))) {
            setError('Ya existe un grupo con ese nombre. Selecciónalo en la lista.');
            return;
        }
        if (groupId && groupId !== NEW_GROUP_ID && !groups.some((group) => String(group.id) === groupId)) {
            setError('El grupo seleccionado ya no está disponible. Selecciona otro grupo.');
            return;
        }
        setSaving(true);
        setError('');
        try {
            let varietyImage = null;
            if (file) {
                const uploaded = await uploadFile(file);
                varietyImage = uploaded?.url || null;
            }
            const created = await addVarietyToProduct(branchId, {
                productId: product.id,
                varietyName: name,
                description: description.trim() || null,
                varietyImage,
                inventoryBaseUnit,
                ...(groupId === NEW_GROUP_ID ? { newGroupName: groupName } : groupId ? { groupId: Number(groupId) } : {}),
                formats: (drafts[0]?.formats || []).map((formatDraft) => {
                    const { varietyId: _varietyId, ...payload } = buildFormatPayload({ formatDraft });
                    return payload;
                }),
            });
            await onVarietyCreated?.(created);
            onClose?.();
        } catch (requestError) {
            setError(requestError?.response?.data?.message || 'No se pudo crear la variedad.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[1800] flex items-center justify-center bg-slate-950/60 p-4">
            <form role="dialog" aria-modal="true" aria-labelledby="new-variety-title" onSubmit={handleSubmit} className="max-h-[90dvh] overflow-y-auto w-full max-w-lg rounded-2xl bg-white shadow-2xl">
                <header className="border-b px-5 py-4">
                    <h2 id="new-variety-title" className="text-lg font-semibold text-gray-950">Nueva variedad</h2>
                    <p className="mt-1 text-xs text-gray-500">
                        Se crean los formatos seleccionados en la categoría. Puedes completar la unidad, equivalencias y precios después en Lista de precios.
                    </p>
                </header>
                <div className="grid gap-4 px-5 py-5 sm:grid-cols-2">
                    <label className="text-xs font-semibold text-gray-700 sm:col-span-2">Nombre
                        <input autoFocus value={varietyName} onChange={(event) => setVarietyName(event.target.value)} className="mt-1 w-full rounded-lg border px-3 py-2 text-sm" />
                    </label>
                    <label className="text-xs font-semibold text-gray-700 sm:col-span-2">Descripción
                        <textarea value={description} onChange={(event) => setDescription(event.target.value)} className="mt-1 w-full rounded-lg border px-3 py-2 text-sm" rows={3} />
                    </label>
                    <label className="text-xs font-semibold text-gray-700">Unidad base de inventario
                        <select value={inventoryBaseUnit} onChange={(event) => setInventoryBaseUnit(event.target.value)} className="mt-1 w-full rounded-lg border px-3 py-2 text-sm">
                            <option value="kg">Kilogramo</option>
                            <option value="unidad">Unidad</option>
                        </select>
                    </label>
                    <label className="text-xs font-semibold text-gray-700">Imagen
                        <input type="file" accept="image/*" onChange={(event) => setFile(event.target.files?.[0] || null)} className="mt-1 w-full text-xs" />
                    </label>
                    <div className="sm:col-span-2 space-y-3 rounded-xl border border-gray-200 bg-gray-50 p-3">
                        <label className="block text-xs font-semibold text-gray-700">Grupo
                            <select aria-label="Grupo" value={groupId} disabled={saving || !canUpdateProduct} onChange={(event) => { setGroupId(event.target.value); setError(''); }} className="mt-1 w-full rounded-lg border bg-white px-3 py-2 text-sm">
                                <option value="">Sin grupo</option>
                                {groups.map((group) => <option key={group.id} value={String(group.id)}>{group.name}</option>)}
                                <option value={NEW_GROUP_ID}>+ Crear nuevo grupo</option>
                            </select>
                        </label>
                        {groupId === NEW_GROUP_ID && <label className="block text-xs font-semibold text-gray-700">Nombre del nuevo grupo
                            <input aria-label="Nombre del nuevo grupo" value={newGroupName} disabled={saving || !canUpdateProduct} onChange={(event) => setNewGroupName(event.target.value)} className="mt-1 w-full rounded-lg border px-3 py-2 text-sm" placeholder="Ej. Rojas o Grandes" />
                        </label>}
                        <p className="text-xs text-gray-500">La variedad quedará dentro del grupo al crearla.</p>
                    </div>
                    <div className="sm:col-span-2 space-y-3">
                        <p className="text-xs font-semibold text-gray-700">Formatos y códigos propuestos</p>
                        {defaults.loading && <p role="status" className="text-xs">Proponiendo formatos...</p>}
                        {defaults.error && <p role="alert" className="text-xs text-red-700">{defaults.error} <button type="button" onClick={defaults.retry} className="underline">Reintentar</button></p>}
                        {!defaults.loading && !defaults.error && drafts[0]?.formatsInitialized && !drafts[0].formats.length && <p className="text-xs text-gray-600">Esta categoría no tiene formatos para prellenar. Puedes añadirlos después.</p>}
                        {(drafts[0]?.formats || []).map((format) => <label key={format._draftKey} className="flex items-center justify-between gap-3 text-sm">{format.productFormatName || format.productFormatCode}
                            <input aria-label={`Código de barras ${format.productFormatCode}`} inputMode="numeric" maxLength={7} value={format.barcode} disabled={saving}
                                onChange={(event) => { const value = event.target.value.replace(/\D/g, '').slice(0, 7); setDrafts((current) => current.map((draft) => ({ ...draft, formats: draft.formats.map((entry) => entry._draftKey === format._draftKey ? { ...entry, barcode: value, barcodeIsSuggested: false } : entry) }))); }}
                                className="w-40 rounded-lg border px-3 py-2" />
                        </label>)}
                        <p className="text-xs text-gray-500">Los códigos nuevos son únicos en toda la empresa. Los campos pendientes se guardarán como borrador.</p>
                    </div>
                    {error && <p role="alert" className="text-xs text-red-600 sm:col-span-2">{error}</p>}
                </div>
                <footer className="flex justify-end gap-2 border-t px-5 py-4">
                    <button type="button" disabled={saving} onClick={onClose} className="rounded-lg border px-4 py-2 text-sm font-semibold text-gray-700">Cancelar</button>
                    <button type="submit" disabled={saving || !canUpdateProduct || defaults.loading || Boolean(defaults.error)} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{saving ? 'Guardando...' : 'Crear variedad'}</button>
                </footer>
            </form>
        </div>
    );
};

export default NewVarietyModal;
